import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import './conversation-history.scss';

interface ConversationLogEntry {
  id: string;
  user_id: string | null;
  timestamp: string;
  message_type: string;
  role: 'user' | 'bot';
  content: string;
  session_id: string;
  created_at: string;
}

interface ConversationSession {
  session_id: string;
  first_message: string;
  last_message: string;
  message_count: number;
}

const ConversationHistory: React.FC = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<ConversationSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [conversationLogs, setConversationLogs] = useState<ConversationLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch conversation sessions
  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      try {
        // Get unique session IDs with first and last message timestamps
        const { data, error } = await supabase
          .from('conversation_logs')
          .select('session_id, timestamp, content')
          .order('timestamp', { ascending: false })
          .eq('user_id', user?.id || null);

        if (error) {
          throw error;
        }

        if (data) {
          // Group by session_id and get first/last message for each session
          const sessionMap = new Map<string, { 
            messages: { timestamp: string }[], 
            content: string 
          }>();
          
          for (const log of data) {
            if (!sessionMap.has(log.session_id)) {
              sessionMap.set(log.session_id, { 
                messages: [{ timestamp: log.timestamp }],
                content: log.content || 'Conversation'
              });
            } else {
              const session = sessionMap.get(log.session_id);
              if (session) {
                session.messages.push({ timestamp: log.timestamp });
              }
            }
          }
          
          const sessionList: ConversationSession[] = Array.from(sessionMap.entries()).map(([session_id, data]) => {
            const timestamps = data.messages.map(m => new Date(m.timestamp).getTime());
            return {
              session_id,
              first_message: new Date(Math.min(...timestamps)).toLocaleString(),
              last_message: new Date(Math.max(...timestamps)).toLocaleString(),
              message_count: data.messages.length
            };
          });
          
          setSessions(sessionList);
        }
      } catch (error) {
        console.error('Error fetching conversation sessions:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchSessions();
    }
  }, [user]);

  // Fetch conversation logs for a specific session
  const fetchConversationLogs = async (sessionId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('conversation_logs')
        .select('*')
        .eq('session_id', sessionId)
        .order('timestamp', { ascending: true });

      if (error) {
        throw error;
      }

      setConversationLogs(data || []);
      setSelectedSession(sessionId);
    } catch (error) {
      console.error('Error fetching conversation logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format message content for display
  const formatMessageContent = (entry: ConversationLogEntry) => {
    try {
      // Try to parse as JSON if it looks like JSON
      if (entry.content.trim().startsWith('{') || entry.content.trim().startsWith('[')) {
        const parsed = JSON.parse(entry.content);
        return <pre>{JSON.stringify(parsed, null, 2)}</pre>;
      }
    } catch (e) {
      // Not JSON, continue with normal text
    }
    
    return <p>{entry.content}</p>;
  };

  return (
    <div className="conversation-history">
      <h2>Conversation History</h2>
      
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="history-container">
          <div className="session-list">
            <h3>Recent Conversations</h3>
            {sessions.length === 0 ? (
              <p>No conversation history found.</p>
            ) : (
              sessions.map((session) => (
                <div 
                  key={session.session_id} 
                  className={`session-item ${selectedSession === session.session_id ? 'selected' : ''}`}
                  onClick={() => fetchConversationLogs(session.session_id)}
                >
                  <div className="session-info">
                    <span className="session-date">{session.first_message}</span>
                    <span className="message-count">{session.message_count} messages</span>
                  </div>
                  <span className="session-last-message">Last activity: {session.last_message}</span>
                </div>
              ))
            )}
          </div>
          
          {selectedSession && (
            <div className="conversation-detail">
              <h3>Conversation</h3>
              <div className="messages">
                {conversationLogs.map((log) => (
                  <div key={log.id} className={`message ${log.role}`}>
                    <div className="message-header">
                      <span className="role">{log.role === 'user' ? 'You' : 'Jarvis'}</span>
                      <span className="timestamp">{new Date(log.timestamp).toLocaleString()}</span>
                    </div>
                    <div className="message-content">
                      {formatMessageContent(log)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConversationHistory;
